import { PayloadAction } from '@reduxjs/toolkit';
import { ERuleSaveStatus } from '../../../data/Constants';
import { isArraysEqual } from '../../../services/Util';
import type {
  IEffectivityResult,
  IFeature,
  IFeatureMaPayload,
  IFeaturesAssociations,
  IMarketAssociation,
  IProcosState,
} from '../../../../types';
import type { ISaveRuleResponse } from '../../../../types/IApiTypes';

/**
 * To handle Feature association checkbox selected
 * @param {IProcosState} state is Procos data
 * @param {action} action is redux action
 * @param {featureKey} featureKey is feature value
 * @returns {void}
 */
export function handleCheckBoxSelected(
  state: IProcosState,
  action: PayloadAction<IFeatureMaPayload>,
  featureKey: IMarketAssociation
): void {
  state.features.ma[action.payload.id].updatedMarkets.push(action.payload.market);

  /*When initial countries length is 0 which represents it was added by user and the selections are
                     same as product maket selections, delete the association as there is no need to add rule to it */
  if (
    featureKey.initialMarkets.length === 0 &&
    state.product.ma?.updatedMarkets.length === featureKey.updatedMarkets.length
  ) {
    delete state.features.ma[action.payload.id];
  }
}

/**
 * To handle Feature association checkbox unselected
 * @param {IProcosState} state is Procos data
 * @param {action} action is redux action
 * @param {featureKey} featureKey is feature value
 * @returns {void}
 */
export function handleCheckBoxUnselect(
  state: IProcosState,
  action: PayloadAction<IFeatureMaPayload>,
  featureKey: IMarketAssociation
) {
  const index = featureKey.updatedMarkets.indexOf(action.payload.market);

  /*When initial countries length is 0 which represents it was added by user and it is the last selection unchecked,
                    delete the association as there is no need to add rule to it */
  if (
    state.features.ma[action.payload.id].initialMarkets.length === 0 &&
    state.features.ma[action.payload.id].updatedMarkets.length === 1
  ) {
    delete state.features.ma[action.payload.id];
    if (state.features.status.updated) {
      state.features.status.updated[action.payload.id] = false;
    }
    return;
  }
  state.features.ma[action.payload.id].updatedMarkets.splice(index, 1);
  if (state.features.ma[action.payload.id].updatedMarkets.length === 0) {
    if (state.features.status.updated) {
      state.features.status.updated[action.payload.id] = false;
    }
  }
}

/**
 * reset status of feature save status
 * @param {IProcosState} state procos state
 * @returns {void}
 */
export function resetFeatureSaveStatus(state: IProcosState) {
  const featuresData = state.features.data;
  featuresData?.map((feature: IFeature) => {
    if (feature.saveStatus) {
      feature.saveStatus = '';
    }
  });

  state.features.data = featuresData;
}

/**
 * Checks if all market arrays in the feature data have the same length for initial and updated markets.
 *
 * @param {IFeaturesAssociations} featureData - The feature data containing each feature data.
 * @returns {boolean} - Returns `true` if all market arrays have equal lengths, otherwise `false`.
 */
export function isAllFeatureMarketEqual(featureData: IFeaturesAssociations): boolean {
  return Object.values(featureData).every(({ initialMarkets, updatedMarkets }) =>
    isArraysEqual(initialMarkets, updatedMarkets)
  );
}

/**
 * Compares two objects representing active statuses and checks if they have the same keys and values.
 *
 * @param {Record<string, boolean>} initial - The initial active status object.
 * @param {Record<string, boolean>} updated - The updated active status object.
 * @returns {boolean} - Returns `true` if both objects have identical keys and values, otherwise `false`.
 */
export function compareActiveStatus(
  initial: { [key: string]: boolean },
  updated: { [key: string]: boolean }
): boolean {
  const initialKeys = Object.keys(initial);
  const updatedKeys = Object.keys(updated);
  // Check if both objects have the same keys
  if (initialKeys.length !== updatedKeys.length) {
    return false;
  }

  // Check if values for each key match
  return initialKeys.every((key) => initial[key] === updated[key]);
}

/**
 * To update Save status of feature effectivity once the API is processed
 * @param {IProcosState} state procos state
 * @param {[]} effectivityFeatures is all feature codes sent to Effectivity API calll
 * @param {IEffectivityResult} responseData is response from API
 * @returns {void}
 */
export function updateSaveStatusOfEffectivity(
  state: IProcosState,
  effectivityFeatures: string[],
  responseData: IEffectivityResult = {}
) {
  const featuresData = state.features.data;
  // There are 3 cases to be covered here
  //Case 1: When all Effectivities are processed successfully - No codes are returned :: StatusCode: 200
  //Case 2: When Effectivities are partially processed successfully - Codes are returned as Dictionary :: StatusCode: 200
  //Case 3: When an exception is thrown by the API ie., no effectivities are processed ( failed ) :: StatusCode: other than 200
  const responseCodes: string[] = Object.keys(responseData) || []; //Case 1 & 2
  const isEffectivityFailedForAll = Object.keys(responseData || {}).includes(
    'AllEffectivityFailed'
  ); //Case 3

  featuresData?.map((feature: IFeature) => {
    for (const code of effectivityFeatures) {
      if (feature.code === code || code.includes(feature.code)) {
        const isEffectivityFailedForCode = responseCodes?.includes(code);
        feature.saveStatus =
          isEffectivityFailedForCode || isEffectivityFailedForAll
            ? ERuleSaveStatus.Failed
            : ERuleSaveStatus.Success;
      }
    }
  });
  state.features.data = featuresData;
}

/**
 * To update Save status of feature market rules
 * @param {IProcosState} state procos state
 * @param {ISaveRuleResponse} saveResponse is an array of failed rules
 * @param {[]} marketRuleFeatureCodes is an array of feature codes sent to API
 * @returns {void}
 */
export function updateSaveStatusOfFeatureRules(
  state: IProcosState,
  saveResponse: ISaveRuleResponse,
  marketRuleFeatureCodes: string[]
): void {
  // Ensure we iterate over an array; `features.data` can be null
  const features: IFeature[] = state.features.data ?? [];

  // There are 3 cases to be covered here
  //Case 1: When all Market Rules are processed successfully - Successful feature codes returned :: StatusCode: 200
  //Case 2: When Market rules are partially processed successfully - Success and failed Codes are returned :: StatusCode: 200
  //Case 3: When an exception is thrown by the API ie., no Rules are processed ( failed ) :: StatusCode: other than 200

  // check whether the feature code available in the list
  const hasFeature = (code: string, list: string[]) =>
    list.includes(code) || list.some((item) => item.includes(code));

  for (const feature of features) {
    const code = feature.code;

    const hasfeatureCode = hasFeature(code, marketRuleFeatureCodes);

    if (hasfeatureCode) {
      const isRuleFailed = hasFeature(code, saveResponse.failed.rules);
      const isPropertyFailed = hasFeature(code, saveResponse.failed.properties);

      //updated the partial failed count
      feature.failed = {
        isPropertyFailed: isPropertyFailed,
        isRuleFailed: isRuleFailed,
      };

      if (isRuleFailed && isPropertyFailed) {
        feature.saveStatus = ERuleSaveStatus.Failed;
      } else if (isRuleFailed || isPropertyFailed) {
        feature.saveStatus = ERuleSaveStatus.Partial;
      } else {
        feature.saveStatus = ERuleSaveStatus.Success;
      }
    }
  }

  state.features.data = features;
}

/**
 * Update Save Status of batches of Create Rules API request
 * @param {IProcosState} state procos state
 * @param { ISaveRuleResponse } saveResponse is an array of failed rules
 * @returns {void}
 */
export function updateSaveStatusOfBatches(state: IProcosState, saveResponse: ISaveRuleResponse) {
  const batchSaveData = state.batchSaveStatus;
  const updateBatch = batchSaveData.find((item) => item.batchStatus === 'P');

  if (!saveResponse?.isSuccess) {
    if (updateBatch) {
      updateBatch.batchStatus = 'F';
    }
  } else if (updateBatch) {
    updateBatch.batchStatus = 'S';
  }
}
